<template>
  <modal-inner class="modal__inner-1--settings" aria-label="Settings">
    <div class="modal__content">
      <div class="tabs flex flex--row">
        <tab :active="tab === 'appearance'" @click="tab = 'appearance'">
          Appearance
        </tab>
        <tab :active="tab === 'editor'" @click="tab = 'editor'">
          Editor
        </tab>
        <tab :active="tab === 'advanced'" @click="tab = 'advanced'">
          Advanced
        </tab>
      </div>

      <!-- Appearance Tab -->
      <div v-if="tab === 'appearance'" class="settings-panel" role="tabpanel">
        <div class="form-entry">
          <label class="form-entry__label">Color Theme</label>
          <div class="form-entry__field">
            <select class="textfield" v-model="settings.colorTheme">
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="sepia">Sepia</option>
              <option value="nord">Nord</option>
              <option value="solarized-light">Solarized Light</option>
              <option value="solarized-dark">Solarized Dark</option>
              <option value="dracula">Dracula</option>
              <option value="monokai">Monokai</option>
            </select>
          </div>
        </div>

        <div class="form-entry">
          <label class="form-entry__label">Font Size</label>
          <div class="form-entry__field form-entry__field--range">
            <input
              type="range"
              min="0.8"
              max="1.4"
              step="0.05"
              v-model.number="settings.fontSizeFactor"
            />
            <span class="range-value">{{ Math.round(settings.fontSizeFactor * 100) }}%</span>
          </div>
        </div>

        <div class="form-entry">
          <label class="form-entry__label">Max Text Width</label>
          <div class="form-entry__field form-entry__field--range">
            <input
              type="range"
              min="0.8"
              max="1.4"
              step="0.05"
              v-model.number="settings.maxWidthFactor"
            />
            <span class="range-value">{{ Math.round(settings.maxWidthFactor * 100) }}%</span>
          </div>
        </div>
      </div>

      <!-- Editor Tab -->
      <div v-else-if="tab === 'editor'" class="settings-panel" role="tabpanel">
        <div class="form-entry">
          <label class="form-entry__label form-entry__label--checkbox">
            <input type="checkbox" v-model="settings.editor.listAutoNumber" />
            Automatic list numbering
          </label>
        </div>

        <div class="form-entry">
          <label class="form-entry__label form-entry__label--checkbox">
            <input type="checkbox" v-model="settings.editor.inlineImages" />
            Display images in editor
          </label>
        </div>

        <div class="form-entry">
          <label class="form-entry__label form-entry__label--checkbox">
            <input type="checkbox" v-model="settings.editor.monospacedFontOnly" />
            Use monospaced font only
          </label>
        </div>
      </div>

      <!-- Advanced Tab (YAML) -->
      <div v-else-if="tab === 'advanced'" class="settings-panel" role="tabpanel">
        <p class="settings-note">Advanced settings in YAML format. Edit carefully.</p>
        <div class="form-entry">
          <div class="form-entry__field form-entry__field--code-editor">
            <code-editor lang="yaml" :value="customSettings" key="custom-settings" @changed="setCustomSettings"></code-editor>
          </div>
        </div>
        <div class="modal__error modal__error--settings">{{ error }}</div>
        <div class="settings-hint">
          <p><strong>Available settings:</strong></p>
          <ul>
            <li><code>shortcuts</code> - Keyboard shortcuts configuration</li>
            <li><code>turndown</code> - HTML to Markdown converter options (headingStyle, hr, bulletListMarker, codeBlockStyle, fence, emDelimiter, strongDelimiter, linkStyle)</li>
            <li><code>newFileContent</code> - Default content for new files</li>
            <li><code>newFileProperties</code> - Default properties for new files</li>
          </ul>
        </div>
      </div>
    </div>
  </modal-inner>
</template>

<script>
import yaml from 'js-yaml';
import { mapGetters } from 'vuex';
import ModalInner from './common/ModalInner.vue';
import Tab from './common/Tab.vue';
import CodeEditor from '../CodeEditor.vue';
import store from '../../store';

const defaultSettingsObj = {
  colorTheme: 'solarized-light',
  fontSizeFactor: 1,
  maxWidthFactor: 1,
  editor: {
    listAutoNumber: true,
    inlineImages: true,
    monospacedFontOnly: false,
  },
};

export default {
  components: {
    ModalInner,
    Tab,
    CodeEditor,
  },
  data: () => ({
    tab: 'appearance',
    settings: JSON.parse(JSON.stringify(defaultSettingsObj)),
    customSettings: '',
    error: null,
  }),
  computed: {
    ...mapGetters('modal', [
      'config',
    ]),
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        this.syncSettingsToYaml();
        this.applySettingsImmediately();
      },
    },
  },
  created() {
    const settingsYaml = store.getters['data/settings'];
    this.customSettings = settingsYaml === '\n' ? '' : settingsYaml;
    this.parseYamlToSettings();
  },
  methods: {
    parseYamlToSettings() {
      try {
        const parsed = yaml.load(this.customSettings) || {};
        const computedSettings = store.getters['data/computedSettings'] || {};
        this.settings = {
          colorTheme: parsed.colorTheme || computedSettings.colorTheme || defaultSettingsObj.colorTheme,
          fontSizeFactor: parsed.fontSizeFactor ?? computedSettings.fontSizeFactor ?? defaultSettingsObj.fontSizeFactor,
          maxWidthFactor: parsed.maxWidthFactor ?? computedSettings.maxWidthFactor ?? defaultSettingsObj.maxWidthFactor,
          editor: {
            listAutoNumber: parsed.editor?.listAutoNumber ?? computedSettings.editor?.listAutoNumber ?? defaultSettingsObj.editor.listAutoNumber,
            inlineImages: parsed.editor?.inlineImages ?? computedSettings.editor?.inlineImages ?? defaultSettingsObj.editor.inlineImages,
            monospacedFontOnly: parsed.editor?.monospacedFontOnly ?? computedSettings.editor?.monospacedFontOnly ?? defaultSettingsObj.editor.monospacedFontOnly,
          },
        };
        this.error = null;
      } catch (e) {
        this.error = e.message;
      }
    },
    syncSettingsToYaml() {
      try {
        const currentYaml = yaml.load(this.customSettings) || {};
        const merged = {
          ...currentYaml,
          colorTheme: this.settings.colorTheme,
          fontSizeFactor: this.settings.fontSizeFactor,
          maxWidthFactor: this.settings.maxWidthFactor,
          editor: {
            ...currentYaml.editor,
            listAutoNumber: this.settings.editor.listAutoNumber,
            inlineImages: this.settings.editor.inlineImages,
            monospacedFontOnly: this.settings.editor.monospacedFontOnly,
          },
        };
        this.customSettings = yaml.dump(merged, { indent: 2 });
        this.error = null;
      } catch (e) {
        this.error = e.message;
      }
    },
    cancel() {
      if (this.config && this.config.reject) {
        this.config.reject();
      }
    },
    setCustomSettings(value) {
      this.customSettings = value;
      this.parseYamlToSettings();
    },
    applySettingsImmediately() {
      if (!this.error) {
        const settings = this.customSettings || '\n';
        store.dispatch('data/setSettings', settings);
      }
    },
    async resolve() {
      if (!this.error) {
        const settings = this.customSettings || '\n';
        await store.dispatch('data/setSettings', settings);
        if (this.config && this.config.resolve) {
          this.config.resolve(settings);
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../styles/variables.scss';

.modal__inner-1.modal__inner-1--settings {
  max-width: 560px;

  .form-entry__field--code-editor {
    .code-editor {
      min-height: 300px;
      max-height: 400px;
    }
  }
}

.settings-panel {
  padding: 10px 0;
}

.settings-note {
  font-size: 0.9em;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 15px;

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    color: rgba(255, 255, 255, 0.5);
  }

  .app--sepia & {
    color: rgba(91, 70, 54, 0.6);
  }

  .app--solarized-light & {
    color: rgba(101, 123, 131, 0.7);
  }
}

.form-entry__label--checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
}

.form-entry__field--range {
  display: flex;
  align-items: center;
  gap: 12px;

  input[type="range"] {
    flex: 1;
    cursor: pointer;
  }

  .range-value {
    min-width: 45px;
    text-align: right;
    font-size: 0.9em;
    color: rgba(0, 0, 0, 0.6);

    .app--dark &,
    .app--nord &,
    .app--solarized-dark &,
    .app--dracula &,
    .app--monokai & {
      color: rgba(255, 255, 255, 0.6);
    }

    .app--sepia & {
      color: rgba(91, 70, 54, 0.7);
    }

    .app--solarized-light & {
      color: rgba(101, 123, 131, 0.8);
    }
  }
}

.modal__error--settings {
  white-space: pre-wrap;
  font-family: $font-family-monospace;
  font-size: $font-size-monospace;
}

.settings-hint {
  margin-top: 16px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  font-size: 0.85em;

  .app--dark &,
  .app--nord &,
  .app--solarized-dark &,
  .app--dracula &,
  .app--monokai & {
    background: rgba(255, 255, 255, 0.05);
  }

  .app--sepia & {
    background: rgba(91, 70, 54, 0.05);
  }

  .app--solarized-light & {
    background: rgba(101, 123, 131, 0.05);
  }

  p {
    margin: 0 0 8px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 4px;
  }

  code {
    background: rgba(0, 0, 0, 0.08);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 0.9em;

    .app--dark &,
    .app--nord &,
    .app--solarized-dark &,
    .app--dracula &,
    .app--monokai & {
      background: rgba(255, 255, 255, 0.1);
    }

    .app--sepia & {
      background: rgba(91, 70, 54, 0.1);
    }
  }
}
</style>
